import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  OnInit,
  signal,
  inject,
  effect,
  ViewChildren,
  ViewChild,
  QueryList,
  ElementRef,
  HostListener,
  NgZone,
  viewChild
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { INotificationEvent } from "../../models";
import { NotificationService } from "../../services/notification.service";
import { slideFromRight } from "../../core-ui.animations";
import { UserService, DeviceService, AssistantService } from "../../services";
import { NotificationTypeEnum, UserRoleEnum } from "../../core-ui.enums";
import { ApplicationService } from "../../services/application.service";
import { NotificationComponent } from "../notification/notification.component";

@Component({
  selector: "app-notification-manager",
  templateUrl: "./notification-manager.component.html",
  styleUrls: ["./notification-manager.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideFromRight],
  standalone: false
})
export class NotificationManagerComponent {
  private userService = inject(UserService);
  private assistant = inject(AssistantService);
  private application = inject(ApplicationService);
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);

  notifications = signal<INotificationEvent[]>([]);

  @HostBinding("attr.aria-live") ariaLive = "polite";

  isOpen = signal(false);

  /** a non-dismissable notification requires an action, so it can neither be removed nor hidden */
  hasRequiredNotification = computed(() =>
    this.notifications().some((n) => n.isDismissible?.() === false)
  );

  @ViewChildren(NotificationComponent)
  notificationComponents: QueryList<NotificationComponent>;

  @ViewChild("toggleBtn", { read: ElementRef })
  toggleBtnRef: ElementRef<HTMLButtonElement>;

  readonly listRef = viewChild<ElementRef<HTMLDivElement>>("notificationList");

  /** the notifications do not fit on screen, so we have to scroll (and therefore clip) them */
  isScrollable = signal(false);

  @HostBinding("class") get class() {
    return {
      "assistant-open": this.isAssistantOpen(),
      talkdesk: this.application.isTalkdeskApp,
      open: this.isOpen(),
      scrollable: this.isScrollable()
    };
  }

  isAssistantOpen = signal(false);

  constructor() {
    const notificationService = inject(NotificationService);
    const cd = inject(ChangeDetectorRef);

    this.assistant.openChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((o) => this.isAssistantOpen.set(o));
    notificationService.notificationReady$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((n) => {
        if (n) {
          this.notifications.update((existing) => {
            return [...existing, n];
          });
          // this.notifications = [...this.notifications, n];
          this.isOpen.set(true);
          cd.detectChanges();
        }
        if (n.getTimeToLive() > 0) {
          setTimeout(() => {
            this.remove(n);
            cd.detectChanges();
          }, n.getTimeToLive());
        }
      });
    notificationService.notificationClearAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        if (type) {
          this.notifications.update((existing) => {
            return existing.filter((n) => n.getType() !== type);
          });
          // this.notifications = this.notifications.filter(
          //   (n) => n.getType() !== type
          // );
        } else {
          this.notifications.set([]);
          // this.notifications = [];
        }
        cd.detectChanges();
      });
    notificationService.notificationDismiss$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((n) => {
        this.remove(n);
        cd.detectChanges();
      });

    effect(() => {
      if (this.isOpen() && this.notifications().length > 0) {
        setTimeout(() => this.focusNotification(0));
      }
    });

    // the notifications overflow the screen only some of the time. we cannot scroll them
    // permanently, because that clips the shadow of every card, so watch their size instead.
    effect((onCleanup) => {
      const list = this.listRef()?.nativeElement;

      if (!list) {
        this.isScrollable.set(false);
        return;
      }

      const observer = new ResizeObserver(() => this.syncScrollable());
      this.zone.runOutsideAngular(() => observer.observe(list));
      onCleanup(() => observer.disconnect());
    });
  }

  @HostListener("window:resize")
  onWindowResize() {
    this.syncScrollable();
  }

  private syncScrollable() {
    const host = this.host.nativeElement;
    // the host height is capped by a max-height, so the content overflows it when it does not fit
    this.isScrollable.set(host.scrollHeight > host.clientHeight);
  }

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent) {
    if (!this.isOpen()) return;
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    const components = this.notificationComponents?.toArray() ?? [];
    if (components.length === 0) return;

    const focusedIndex = components.findIndex(
      (c) =>
        c.hostEl === document.activeElement ||
        c.hostEl.contains(document.activeElement)
    );

    event.preventDefault();

    if (event.key === "ArrowDown") {
      const next = focusedIndex < components.length - 1 ? focusedIndex + 1 : 0;
      components[next].focus();
    } else {
      const prev = focusedIndex > 0 ? focusedIndex - 1 : components.length - 1;
      components[prev].focus();
    }
  }

  trackByFn(index, n: INotificationEvent) {
    return n?.getId();
  }

  remove(notification: INotificationEvent) {
    if (notification?.isDismissible?.() === false) {
      return;
    }

    const index = this.notifications().findIndex(
      (n) => n.getId() === notification.getId()
    );
    this.notifications.update((existing) =>
      existing.filter((n) => n.getId() !== notification.getId())
    );

    setTimeout(() => {
      const remaining = this.notificationComponents?.toArray() ?? [];
      if (remaining.length > 0) {
        remaining[Math.min(index, remaining.length - 1)].focus();
      } else {
        this.toggleBtnRef?.nativeElement?.focus();
      }
    });
  }

  focusNotification(index: number) {
    const components = this.notificationComponents?.toArray() ?? [];
    components[index]?.focus();
  }

  toggle() {
    if (this.hasRequiredNotification()) {
      return;
    }
    this.isOpen.set(!this.isOpen());
  }

  get isWidget() {
    const isCustomer = this.userService
      .getActiveUser()
      ?.hasRole(UserRoleEnum.customer);
    return DeviceService.isIFrame && isCustomer;
  }

  get isToggleNotificationsVisible() {
    return computed(() => {
      return (
        !this.hasRequiredNotification() &&
        this.notifications().filter(
          (n) => n.getType() !== NotificationTypeEnum.toast
        ).length > 0
      );
    });
  }
}
